import { Box, ButtonBase, TableRow, Theme, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import InfoCircleIcon from '@src/icons/InfoCircleIcon'
import EditIcon from '@src/icons/EditIcon'
import CloseIcon from '@material-ui/icons/Close'
import { getDisplayName, truncateLongString } from '../utils'
import { messages } from '@src/routes/Configurators/messages'
import HoverToolTip from '@src/components/HoverToolTip/HoverTooltipForForms'
import {
  FormattedMessage,
  FormattedNumber,
  IntlShape,
  useIntl,
} from 'react-intl'
import { isNil } from 'lodash'
import StyledTableCell from './StyledTableCell'
import { useProductFromQueryParam } from '../ProductsConfiguration'
import { getConfirmOrderTableItemsByProduct } from './ConfirmOrderTableMap'
import WarningRoundedIcon from '@material-ui/icons/WarningRounded'
import { TubeProperties } from '../../../utils/enum'
import { TubeProduct } from '../../../utils/StandardMTOEnum'
import { useUserSession } from '@src/utils/useUserSession'
import useFormikHelper from '../useFormikHelper'

const useStyle = makeStyles((theme: Theme) => {
  return {
    closeIconStyle: {
      fontSize: theme.typography.pxToRem(12),
      cursor: 'pointer',
      fontWeight: 'bold',
      stroke: 'currentColor',
      strokeWidth: 2,
      marginLeft: theme.spacing(4),
    },
    editIconStyle: {
      fontSize: theme.typography.pxToRem(16),
      cursor: 'pointer',
    },
    linkStyle: {
      fontWeight: 'normal',
      cursor: 'pointer',
    },
    infoIconStyle: {
      fontSize: theme.typography.pxToRem(16),
      cursor: 'pointer',
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.error.main,
      display: 'inline',
      marginRight: theme.spacing(1.75),
    },
    warningIconStyle: {
      fontSize: theme.typography.pxToRem(18),
      cursor: 'pointer',
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.warning.main,
      display: 'inline',
      marginRight: theme.spacing(1.75),
    },
    ghostIcon: {
      width: theme.spacing(3),
      height: theme.spacing(3),
      display: 'inline-block',
      marginRight: theme.spacing(2.75),
    },
    nameStyle: {
      display: 'flex',
      justifyContent: 'space-between',
      width: theme.spacing(22),
      gap: theme.spacing(1.25),
    },
  }
})

interface OligoTableRowProps {
  oligo: any
  index: number
  displayConcentrationColumn?: boolean
  handleEditIconClick: (index: number) => void
  handleCloseIconClick: (index: number) => void
  hasError: boolean
  hasWarning: boolean
}

const defaultValue = '-'

const renderValue = (v?: string | number) => {
  return v || defaultValue
}

const renderPrice = (
  price: string | undefined,
  currency: string | undefined
) => {
  if (!isNil(price)) {
    return price.toLowerCase().includes('quote') ? (
      <FormattedMessage id="QUOTE_ITEM" defaultMessage="Quote Item" />
    ) : (
      <FormattedNumber
        style="currency"
        currencyDisplay="symbol"
        currency={currency}
        value={Number(price)}
      />
    )
  }

  return <span>-</span>
}

const OligoTableRow: React.FC<OligoTableRowProps> = ({
  oligo,
  index,
  displayConcentrationColumn,
  handleEditIconClick,
  handleCloseIconClick,
  hasError,
  hasWarning,
}) => {
  const classes = useStyle()
  const intl = useIntl()
  const { userSession } = useUserSession()
  const product = useProductFromQueryParam()
  const formik = useFormikHelper()

  // warning icon displays when there is no error for all oligos
  const noOligoError = !formik.allValues.oligoList?.find(
    (val) => val?.listErrors?.length
  )
  let confirmOrderTableItems = getConfirmOrderTableItemsByProduct(product)
  if (displayConcentrationColumn === false) {
    confirmOrderTableItems = confirmOrderTableItems.filter(
      (item) => item.id !== 'CONCENTRATION'
    )
  }
  const getOligoDisplayKey = (
    oligo: any,
    item: { oligoObjPropName: string },
    product: string,
    intl: IntlShape
  ): string | string[] => {
    const isIScaleDNA = product === TubeProduct.iscaledna
    const propertyName = item.oligoObjPropName
    const propertyValue = oligo[propertyName]
    const isProductRNA = product === TubeProduct.Rna
    const isProductEasyOligo = product === TubeProduct.EasyOligo
    if (!propertyValue) return defaultValue
    if (propertyName === TubeProperties.Amount && isIScaleDNA) {
      return `_PC_SPEC_${propertyValue}`
    }

    // NOTE: for JP in solution ( water) is displayed as in solution (TE)
    if (
      propertyName === TubeProperties.Format &&
      (isProductRNA || isProductEasyOligo) &&
      userSession.country === 'JP' &&
      propertyValue === TubeProperties.LIQUID
    ) {
      return '_PC_SPEC_IS_TE'
    }

    if (
      propertyName === TubeProperties.AdditionalServices &&
      isIScaleDNA &&
      propertyValue?.length > 0
    ) {
      return propertyValue.map(
        (service: string) =>
          ' ' + intl.formatMessage(messages[`_PC_SPEC_${service}`])
      )
    }

    return propertyValue
  }
  return (
    <TableRow key={oligo.sequence + oligo.name + index}>
      <StyledTableCell
        displayConcentrationColumn={displayConcentrationColumn}
        style={{ paddingInline: '0px' }}
      >
        <Box display="flex" alignItems="center">
          {hasError ? (
            <InfoCircleIcon className={classes.infoIconStyle} />
          ) : hasWarning && noOligoError ? (
            <WarningRoundedIcon className={classes.warningIconStyle} />
          ) : (
            <Box className={classes.ghostIcon} />
          )}
          <ButtonBase
            onClick={() => handleEditIconClick(index)}
            className={classes.nameStyle}
          >
            <Typography
              color="primary"
              variant="caption"
              className={classes.linkStyle}
            >
              {renderValue(truncateLongString(oligo.name, 8))}
            </Typography>
            <EditIcon
              onClick={() => handleEditIconClick(index)}
              className={classes.editIconStyle}
              color="primary"
            />
          </ButtonBase>
        </Box>
      </StyledTableCell>
      <StyledTableCell displayConcentrationColumn={displayConcentrationColumn}>
        {(() => {
          const fullSequence = `${oligo.fiveMod || ''}${oligo.sequence}${
            oligo.threeMod || ''
          }`
          const displaySequence = truncateLongString(fullSequence, 18)
          const isLong = renderValue(fullSequence).toString().length > 18

          const SequenceContent = (
            <Typography
              color="primary"
              variant="caption"
              className={classes.linkStyle}
            >
              {renderValue(displaySequence)}
            </Typography>
          )

          return isLong ? (
            <HoverToolTip toolTipText={renderValue(fullSequence)} delay={1}>
              {SequenceContent}
            </HoverToolTip>
          ) : (
            SequenceContent
          )
        })()}
      </StyledTableCell>
      {confirmOrderTableItems.map((item, index) => {
        const oligoProp = item.oligoObjPropName

        const oligoValue = getOligoDisplayKey(oligo, item, product, intl)
        return (
          <StyledTableCell
            key={index}
            displayConcentrationColumn={displayConcentrationColumn}
            style={{
              width:
                oligoProp === TubeProperties.AdditionalServices
                  ? '180px'
                  : 'none',
            }}
            isServiceColumn={oligoProp === TubeProperties.AdditionalServices}
          >
            {messages[getDisplayName(oligoValue)] ? (
              <FormattedMessage {...messages[getDisplayName(oligoValue)]} />
            ) : (
              renderValue(getDisplayName(oligoValue))
            )}
          </StyledTableCell>
        )
      })}
      <StyledTableCell displayConcentrationColumn={displayConcentrationColumn}>
        <Box display="flex" alignItems="center" justifyContent="right">
          {hasError ? '_' : renderPrice(oligo.price, oligo.currency)}
        </Box>
      </StyledTableCell>
      <StyledTableCell
        displayConcentrationColumn={displayConcentrationColumn}
        style={{
          paddingLeft: 0,
        }}
      >
        <Box display="flex" alignItems="center">
          <CloseIcon
            onClick={() => handleCloseIconClick(index)}
            className={classes.closeIconStyle}
          />
        </Box>
      </StyledTableCell>
    </TableRow>
  )
}

export default OligoTableRow
